import { environment } from 'src/environments/environment';

/**
 * Central helper for everything that depends on the fiscal year selected in the
 * FY dropdown in the application header (see app-header.html / app-header.ts).
 *
 * The selected value is a plain string kept in localStorage under "fiscalyear".
 * Historical values are "FY20" / "FY20SY"; current values are "CY21".."CY27".
 * "-1" is the "--Select--" sentinel from the dropdown.
 *
 * Changing the FY dropdown writes localStorage and then navigates to the
 * default route, which tears down and re-creates the current page. That means
 * callers can safely read these values synchronously at load time (ngOnInit or
 * inside a data-fetch callback) without subscribing to anything.
 */

export const FISCAL_YEAR_STORAGE_KEY = 'fiscalyear';

/**
 * Returns the fiscal year currently selected in the header, seeding the default
 * on first use so that the header dropdown and every consumer always agree.
 *
 * The default is derived from the current calendar year ("CY" + last 2 digits),
 * which is the same rule the header has always used. environment.fiscalyear is
 * only the last-resort fallback.
 */
export function getSelectedFiscalYear(): string {
  const stored = localStorage.getItem(FISCAL_YEAR_STORAGE_KEY);
  if (stored != null && stored !== '') {
    return stored;
  }

  const seeded = getDefaultFiscalYear();
  localStorage.setItem(FISCAL_YEAR_STORAGE_KEY, seeded);
  return seeded;
}

/** The fiscal year to use when nothing has been selected yet. */
export function getDefaultFiscalYear(): string {
  const year = new Date().getFullYear().toString();
  return 'CY' + year.slice(-2);
}

/**
 * Extracts the two-digit year from a fiscal year string, or null when the value
 * is not a recognisable fiscal year (e.g. the "-1" sentinel).
 *
 *   "CY27"   -> 27
 *   "CY26"   -> 26
 *   "FY20SY" -> 20
 *   "-1"     -> null
 */
export function getFiscalYearNumber(fiscalYear: string): number | null {
  const match = /^(?:CY|FY)(\d{2})/.exec((fiscalYear || '').trim().toUpperCase());
  return match ? Number(match[1]) : null;
}

/**
 * True when the given fiscal year is at or after the year in which pay curves
 * were introduced (environment.payCurveMinFiscalYear, "CY27" by default).
 *
 * Pay curve columns, pay curve detail fields and the Mapping Configuration
 * admin area are all gated on this, so that older fiscal years keep showing the
 * UI they had before pay curves existed.
 */
export function arePayCurvesEnabled(fiscalYear: string = getSelectedFiscalYear()): boolean {
  const selected = getFiscalYearNumber(fiscalYear);
  if (selected === null) {
    return false;
  }

  const minimum = getFiscalYearNumber((environment as any).payCurveMinFiscalYear || 'CY27');
  if (minimum === null) {
    return false;
  }

  return selected >= minimum;
}

/** Matches the grid column ids paycurve1..paycurve5. */
const PAY_CURVE_COLUMN_ID = /^paycurve\d$/i;

/**
 * Returns the given Soho datagrid column definitions with the pay curve columns
 * removed when the selected fiscal year predates pay curves.
 *
 * The columns are filtered out rather than flagged `hidden`, because a hidden
 * column is still offered in the datagrid "Personalize Columns" dialog and is
 * still included in "Export to Excel".
 */
export function filterPayCurveColumns<T extends { id?: string }>(columns: T[]): T[] {
  if (arePayCurvesEnabled()) {
    return columns;
  }
  return columns.filter(column => !PAY_CURVE_COLUMN_ID.test(column.id || ''));
}
